import {useInfiniteQuery} from '@tanstack/react-query';
import {useMemo} from 'react';

import {fetchHistory} from '@/api/history';
import {fetchTracksByIds} from '@/api/track';
import {useAppSelector} from '@/hooks/useRedux';
import {useDbQuery} from '@/queries/db';
import {selectActiveUserSigner} from '@/store/user';
import {QueryKeys} from '@/types/queryKeys';
import {getLastItem} from '@/utils/functions';

export const useHistoryQuery = (enableQuery: boolean) => {
  const signer = useAppSelector(selectActiveUserSigner);
  const {db, updateDb} = useDbQuery();

  const query = useInfiniteQuery(
    [QueryKeys.history, signer?.address],
    async ({pageParam}) => {
      const historyTracks = await fetchHistory(signer!, pageParam);
      const historyTracksIds = historyTracks.map(({trackId}) => trackId);
      const tracks = await fetchTracksByIds(historyTracksIds);
      updateDb({tracks});
      return historyTracks;
    },
    {
      enabled: !!signer && enableQuery,
      getNextPageParam: lastPage =>
        lastPage.length < 50 ? undefined : getLastItem(lastPage)?.timestamp,
    },
  );

  const historyTracks = useMemo(
    () =>
      query.data?.pages
        .reduce((allData, currentPage) => allData.concat(currentPage))
        .map(item => ({
          timestamp: item.timestamp,
          track: db.tracks[item.trackId],
        }))
        .filter(item => !!item.track) || [],
    [db.tracks, query.data?.pages],
  );

  return {
    historyTracks,
    query,
  };
};
